import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import ProfileDialog from '@/components/ProfileDialog';

const API = '/api';

const Login = () => {
  const { login, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showForcePassword, setShowForcePassword] = useState(false);
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });

  const handleInputChange = (e) => {
    setCredentials(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    
    if (!credentials.username || !credentials.password) {
      toast.error('Please enter username and password');
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, credentials, {
        withCredentials: true
      });
      
      // Check if password change is required
      if (response.data.must_change_password) {
        toast.warning(
          response.data.password_expired
            ? 'Your password has expired. Please set a new password.'
            : 'You must change your password before continuing.'
        );
        await checkAuth();
        setShowForcePassword(true);
        return;
      }

      toast.success(`Welcome, ${response.data.name}!`);
      await checkAuth();
      
      // Redirect based on role
      const userRole = response.data.role;
      if (userRole === 'TenderUser') {
        navigate('/tenders');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Login error:', error);
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center bg-cover bg-center relative"
      style={{
        backgroundImage: 'url(https://images.unsplash.com/photo-1599417262825-fc78c4784982?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwzfHxpbmR1c3RyaWFsJTIwZGllc2VsJTIwZ2VuZXJhdG9yfGVufDB8fHxibGFja19hbmRfd2hpdGV8MTc2NTk2MzUxNHww&ixlib=rb-4.1.0&q=85)'
      }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-slate-900/70" />
      
      {/* Content */}
      <Card className="w-full max-w-md mx-4 relative z-10 bg-card/95 backdrop-blur-sm">
        <CardHeader className="text-center space-y-2">
          <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-2">
            <img 
              src="/sharda-logo.png" 
              alt="Sharda Logo"
              className="w-16 h-16 object-contain"
            />
          </div>
          <CardTitle className="font-heading text-2xl">Sharda</CardTitle>
          <CardDescription className="text-base">
            Lead Management Dashboard
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Username/Password Form */}
          <form onSubmit={handlePasswordLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                data-testid="login-username"
                name="username"
                type="text"
                placeholder="Enter username"
                value={credentials.username}
                onChange={handleInputChange}
                disabled={isLoading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                data-testid="login-password"
                name="password"
                type="password"
                placeholder="Enter password"
                value={credentials.password}
                onChange={handleInputChange}
                disabled={isLoading}
              />
            </div>
            <Button 
              type="submit" 
              data-testid="login-submit"
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

        </CardContent>
      </Card>

      {/* Force password change dialog */}
      <ProfileDialog
        open={showForcePassword}
        onOpenChange={(open) => {
          if (!open) {
            // Password was changed — redirect
            navigate('/dashboard');
          }
          setShowForcePassword(open);
        }}
        forcePasswordTab
      />
    </div>
  );
};

export default Login;
